import React, { useCallback } from 'react';
import {
  FormControlLabel, Switch, Theme, createStyles, makeStyles,
} from '@material-ui/core';
import { SwitchProps } from '@material-ui/core/Switch';
import {
  FormikValues,
  useField,
} from 'formik';

import { useId } from '../../hooks';
import { ClassApi } from '../../types/styles';

const useStyles = makeStyles(({ palette }: Theme) => createStyles({
  root: {
    marginBottom: 0,
  },
  label: {},
  switchBase: {
    color: palette.switch.color,
    '&$checked': {
      color: palette.menuAction.selected,
    },
    '&$checked + $track': {
      backgroundColor: palette.menuAction.selected,
    },
  },
  checked: {},
  track: palette.switch.track,
  topLabel: {
    fontWeight: 700,
    fontFamily: 'Montserrat',
  },
}), { name: 'FormSwitch' });

type Props<T extends FormikValues> = Omit<SwitchProps, 'classes'> & {
  name: string;
  label: React.ReactNode;
  topLabel?: string;
  classes?: ClassApi<typeof useStyles>;
  onChangeCallback?: (checked: boolean) => void;
};

const FormSwitch = <T extends FormikValues>({
  name,
  label,
  topLabel,
  disabled,
  classes: classesExternal,
  onChangeCallback,
  ...props
}: Props<T>) => {
  const classes = useStyles({ classes: classesExternal });

  const [field] = useField({ name });

  const checkboxId = useId('checkbox-');

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    field.onChange(event);
    if (onChangeCallback) {
      onChangeCallback(checked);
    }
  }, [onChangeCallback, field]);

  return (
    <div>
      {
        topLabel && (
          <div className={classes.topLabel}>{topLabel}</div>
        )
      }
      <FormControlLabel
        disabled={disabled}
        label={label}
        htmlFor={checkboxId}
        classes={{
          root: classes.root,
          label: classes.label,
        }}
        control={(
          <Switch
            id={checkboxId}
            name={name}
            disabled={disabled}
            {...props}
            onChange={handleChange}
            checked={field.value}
            value={field.value}
            classes={{
              switchBase: classes.switchBase,
              checked: classes.checked,
              track: classes.track,
            }}
          />
        )}
      />
    </div>
  );
};

export default FormSwitch;
